import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, Inject, inject, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { NavigationService } from './services/navigation.service';
import { PATH_TRANSLATIONS, SUPPORTED_LANGS, SupportedLang } from '../shared/config/localized-paths';

@Component({
  selector: 'global-header',
  templateUrl: './global-header.component.html',
  styleUrls: ['./global-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalHeaderComponent implements OnInit {
  showGradesMenu = false;

  private readonly gradesCanonicalKeys = ['overview', 'gradesk-1', 'grades2-3', 'grades4-5', 'grade6', 'grades7-8', 'grades9-10', 'grades11-12'];

  /** All localized segments (both EN and ES) that should trigger the grades menu */
  private readonly gradesSegments: string[] = SUPPORTED_LANGS.flatMap(lang =>
    this.gradesCanonicalKeys.map(key => PATH_TRANSLATIONS[lang as SupportedLang][key]),
  );

  readonly isMobileNavOpen$ = this.navigationService.isMobileNavOpen$;

  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser: boolean;

  constructor(
    private readonly el: ElementRef,
    private readonly navigationService: NavigationService,
    private readonly renderer: Renderer2,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateHeaderOffset();
  }

  get hamburgerAriaLabel(): string {
    return this.document.documentElement.lang === 'es'
      ? 'Abrir menú de navegación'
      : 'Open navigation menu';
  }

  get closeNavAriaLabel(): string {
    return this.document.documentElement.lang === 'es'
      ? 'Cerrar menú de navegación'
      : 'Close navigation menu';
  }

  toggleMobileNav(): void {
    this.navigationService.toggleMobileNav();
  }

  private updateGradesMenuBodyClass(): void {
    if (!this.isBrowser) return;
    if (this.showGradesMenu) {
      this.renderer.addClass(this.document.body, 'grades-menu-visible');
    } else {
      this.renderer.removeClass(this.document.body, 'grades-menu-visible');
    }
    this.updateHeaderOffset();
  }

  private updateHeaderOffset(): void {
    if (!this.isBrowser) return;
    requestAnimationFrame(() => {
      const header = this.el.nativeElement.querySelector('.myce-header');
      const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
      this.document.documentElement.style.setProperty('--header-height', `${headerBottom}px`);
    });
  }

  ngOnInit(): void {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          const urlSegments = event.urlAfterRedirects.split('/');
          const isGradesPage = urlSegments.some(segment =>
            this.gradesSegments.includes(segment),
          );
          this.showGradesMenu = isGradesPage;
          this.updateGradesMenuBodyClass();
          // Close mobile drawer on navigation
          this.navigationService.closeMobileNav();
        }
      });

    // Initialise grades menu state from current URL (before first NavigationEnd)
    const urlSegments = this.router.url.split('/');
    this.showGradesMenu = urlSegments.some(segment => this.gradesSegments.includes(segment));
    this.updateGradesMenuBodyClass();

    if (this.isBrowser) {
      this.document.documentElement.classList.add('default-font-size');
    }
  }
}
